/** A calendar in the calendar list: checkbox in the calendar's colour (show/hide in the grid), name, extra content. */
import { useId, type ReactNode } from 'react';
import { Checkbox } from '../components/checkbox';
import { cn } from '../lib/utils';
import type { CalendarColor } from './model';

const CHECKED: Record<CalendarColor, string> = {
  blue: 'border-cal-blue data-[state=checked]:border-cal-blue data-[state=checked]:bg-cal-blue dark:data-[state=checked]:bg-cal-blue',
  green: 'border-cal-green data-[state=checked]:border-cal-green data-[state=checked]:bg-cal-green dark:data-[state=checked]:bg-cal-green',
  purple: 'border-cal-purple data-[state=checked]:border-cal-purple data-[state=checked]:bg-cal-purple dark:data-[state=checked]:bg-cal-purple',
  orange: 'border-cal-orange data-[state=checked]:border-cal-orange data-[state=checked]:bg-cal-orange dark:data-[state=checked]:bg-cal-orange',
  teal: 'border-cal-teal data-[state=checked]:border-cal-teal data-[state=checked]:bg-cal-teal dark:data-[state=checked]:bg-cal-teal',
  pink: 'border-cal-pink data-[state=checked]:border-cal-pink data-[state=checked]:bg-cal-pink dark:data-[state=checked]:bg-cal-pink',
  yellow: 'border-cal-yellow data-[state=checked]:border-cal-yellow data-[state=checked]:bg-cal-yellow dark:data-[state=checked]:bg-cal-yellow',
  red: 'border-cal-red data-[state=checked]:border-cal-red data-[state=checked]:bg-cal-red dark:data-[state=checked]:bg-cal-red',
};

export interface CalendarToggleProps {
  name: string;
  color: CalendarColor;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** Icon before the name (room, equipment, person). */
  icon?: ReactNode;
  /** Small muted text under the name (e.g. "Free/busy only"). */
  note?: string | undefined;
  /** Buttons at the end (more actions, remove). */
  actions?: ReactNode;
  className?: string;
}

export function CalendarToggle({ name, color, checked, onCheckedChange, icon, note, actions, className }: CalendarToggleProps) {
  const id = useId();
  return (
    <div className={cn('group flex min-h-control items-center gap-2 rounded-md px-2 hover:bg-accent', className)}>
      <Checkbox id={id} checked={checked} onCheckedChange={(value) => onCheckedChange(value === true)} className={CHECKED[color]} />
      <label htmlFor={id} className="flex min-w-0 flex-1 cursor-pointer items-center gap-1.5 text-sm">
        {icon && <span className="shrink-0 text-muted-foreground [&_svg]:size-3.5">{icon}</span>}
        <span className="flex min-w-0 flex-col">
          <span className="truncate">{name}</span>
          {note && <span className="truncate text-xs text-muted-foreground">{note}</span>}
        </span>
      </label>
      {actions}
    </div>
  );
}
